import classNames from "classnames";
import { forwardRef, HTMLAttributes } from "react";
import { Flex } from "../../../../flex";
import { FlattenedItem, TreeItemType } from "../types";
import { RedoFolderItem } from "./redo-folder-item";
import { RedoViewItem } from "./redo-view-item";
import * as styles from "./tree-item.module.css";

export interface TreeItemProps
  extends Omit<HTMLAttributes<HTMLLIElement>, "id"> {
  value: FlattenedItem;
  depth: number;
  indentationWidth: number;
  clone?: boolean;
  ghost?: boolean;
  handleProps?: any;
  onToggleExpanded?(): void;
  onRequestEdit?: () => void;
  wrapperRef?(node: HTMLLIElement): void;
  childrenLinks?: string[];
  /** Total direct children; sizes the folder's connector pipe when expanded. */
  childCount?: number;
  expanded?: boolean;
  /** State-driven selection flag. Undefined falls back to URL matching. */
  selected?: boolean;
  /** When provided, body-click fires this instead of URL navigation or toggle. */
  onSelect?: () => void;
  /** When true, drag is disabled and the drag handle is not rendered. */
  disableDrag?: boolean;
}

export const TreeItemComponent = forwardRef<HTMLDivElement, TreeItemProps>(
  function TreeItemComponent(
    {
      clone,
      depth,
      ghost,
      handleProps,
      indentationWidth,
      onToggleExpanded,
      style,
      value,
      wrapperRef,
      onRequestEdit,
      childrenLinks,
      childCount,
      expanded,
      selected,
      onSelect,
      disableDrag,
    },
    ref,
  ) {
    return (
      <Flex
        className={classNames(
          styles.wrapper,
          ghost && styles.ghost,
          clone && styles.clone,
        )}
        gap="none"
        ref={wrapperRef}
        style={
          {
            "--spacing": `${indentationWidth * depth}px`,
          } as React.CSSProperties
        }
      >
        <Flex
          align="center"
          gap="none"
          position="relative"
          ref={ref}
          style={style}
          w="full"
        >
          {value.type === TreeItemType.Folder ? (
            <RedoFolderItem
              childCount={childCount}
              childrenLinks={childrenLinks ?? []}
              clone={clone}
              disableDrag={disableDrag}
              dragHandleProps={handleProps}
              expanded={!!expanded}
              friendlyName={value.friendlyName}
              Icon={value.Icon}
              IconCollapsed={value.IconCollapsed}
              notificationCount={value.notificationCount}
              onClick={onSelect}
              onRequestEdit={onRequestEdit}
              onToggleExpanded={onToggleExpanded ?? (() => {})}
              selected={selected}
            />
          ) : (
            <RedoViewItem
              clone={clone}
              dimmed={value.dimmed}
              disableDrag={disableDrag}
              dragHandleProps={handleProps}
              friendlyName={value.friendlyName}
              Icon={value.Icon}
              linkHref={value.actionHref}
              notificationCount={value.notificationCount}
              onClick={onSelect}
              selected={selected}
            />
          )}
        </Flex>
      </Flex>
    );
  },
);
